/**
 * Customer Action Handlers (Composed)
 *
 * Main coordinator hook that composes all customer action handlers
 * into a unified interface. Provides a complete set of action handlers
 * for both table and drawer contexts.
 */

import { useCallback } from 'react';
import { toast } from 'sonner';

import { CustomerActionHandlers } from '@/sections/customerManagement/types';

import { useCustomerExportHandlers } from './useCustomerExportHandlers';
import { useCustomerScanHandlers } from './useCustomerScanHandlers';
import { useCustomerTypeHandlers } from './useCustomerTypeHandlers';

/**
 * Main coordinator hook for customer action handlers
 *
 * Composes all customer action handlers (type updates, export, share, scan
 * pause/resume) into a unified interface that works for both single and bulk
 * operations.
 *
 * @returns Combined action handlers interface
 *
 * @example
 * ```tsx
 * const handlers = useCustomerActionHandlers();
 *
 * // Update customer type
 * await handlers.handleUpdateType(customers, 'SAAS');
 *
 * // Export customers
 * await handlers.handleExport(customers, 'csv');
 *
 * // Share customer URLs
 * handlers.handleShare(customers);
 * ```
 */
export const useCustomerActionHandlers = (): CustomerActionHandlers => {
  const { handleUpdateCustomerType } = useCustomerTypeHandlers();
  const { handleExportCustomers } = useCustomerExportHandlers();
  const scanHandlers = useCustomerScanHandlers();

  /**
   * Wrapper for customer type updates with user-friendly messaging
   */
  const handleUpdateType: CustomerActionHandlers['handleUpdateType'] =
    useCallback(
      async (customers, type) => {
        const result = await handleUpdateCustomerType(customers, type);

        // Show appropriate toast based on results
        if (result.failed === 0) {
          toast.success(
            `Updated type for ${result.succeeded} customer${result.succeeded > 1 ? 's' : ''}`
          );
        } else if (result.succeeded > 0) {
          toast.warning(
            `Updated type for ${result.succeeded} customer${result.succeeded > 1 ? 's' : ''}, ` +
              `but failed for ${result.failed} customer${result.failed > 1 ? 's' : ''}`
          );
        }
      },
      [handleUpdateCustomerType]
    );

  /**
   * Wrapper for customer export (already has messaging)
   */
  const handleExport: CustomerActionHandlers['handleExport'] = useCallback(
    async (customers, format) => {
      await handleExportCustomers(customers, format);
    },
    [handleExportCustomers]
  );

  /**
   * Shares customer URLs by copying them to clipboard
   *
   * Generates deep links with drawer state that open directly to
   * the customer drawer when visited.
   *
   * @param customers - Customers to share
   */
  const handleShare: CustomerActionHandlers['handleShare'] = useCallback(
    customers => {
      const urls = customers.map(customer => {
        const params = new URLSearchParams();
        params.append('customerDrawerEmail', customer.email);
        params.append('drawerOrder', JSON.stringify(['customer']));
        return `${window.location.origin}${window.location.pathname}?${params.toString()}`;
      });

      navigator.clipboard.writeText(urls.join('\n'));
      toast.success(
        `Copied ${urls.length} customer URL${urls.length > 1 ? 's' : ''} to clipboard`
      );
    },
    []
  );

  /**
   * Wrapper for pause scanning with user-friendly messaging
   */
  const handlePauseScanning: CustomerActionHandlers['handlePauseScanning'] =
    useCallback(
      async customers => {
        const result = await scanHandlers.handlePauseScanning(customers);

        // Show appropriate toast based on results
        if (result.failed === 0) {
          toast.success(
            `Paused scanning for ${result.succeeded} customer${result.succeeded > 1 ? 's' : ''}`
          );
        } else if (result.succeeded > 0) {
          toast.warning(
            `Paused scanning for ${result.succeeded} customer${result.succeeded > 1 ? 's' : ''}, ` +
              `but failed for ${result.failed} customer${result.failed > 1 ? 's' : ''}`
          );
        } else {
          toast.error('Failed to pause scanning for all customers');
        }
      },
      [scanHandlers]
    );

  /**
   * Wrapper for resume scanning with user-friendly messaging
   */
  const handleResumeScanning: CustomerActionHandlers['handleResumeScanning'] =
    useCallback(
      async customers => {
        const result = await scanHandlers.handleResumeScanning(customers);

        // Show appropriate toast based on results
        if (result.failed === 0) {
          toast.success(
            `Resumed scanning for ${result.succeeded} customer${result.succeeded > 1 ? 's' : ''}`
          );
        } else if (result.succeeded > 0) {
          toast.warning(
            `Resumed scanning for ${result.succeeded} customer${result.succeeded > 1 ? 's' : ''}, ` +
              `but failed for ${result.failed} customer${result.failed > 1 ? 's' : ''}`
          );
        } else {
          toast.error('Failed to resume scanning for all customers');
        }
      },
      [scanHandlers]
    );

  return {
    handleUpdateType,
    handleExport,
    handleShare,
    handlePauseScanning,
    handleResumeScanning,
  };
};
